import { useEffect, useRef, useState } from 'react';
import { MaskotElvis, type ElvisLage } from './MaskotElvis';

/** Ögonens mittpunkt i bilden (349 × 535) – mellan vänster och höger öga. */
const OGON_X = 209 / 349;
const OGON_Y = 178 / 535;
/** Längsta blickförskjutning, i bildens pixlar. */
const MAX_BLICK = 10;

/**
 * ELvis kikar upp bakom inloggningsrutan. Han står bakom kortet (föräldern
 * ger kortet högre z-index) och ögonen följer pekaren när han vilar. Läget
 * styrs av sidan: lyssnar medan någon skriver, glad när Microsoft-inloggningen
 * startar, bekymrad vid fel.
 */
export function ElvisTittarFram({ lage }: { lage: ElvisLage }) {
  const ref = useRef<HTMLDivElement>(null);
  const [blick, setBlick] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const fin = window.matchMedia('(pointer: fine)').matches;
    const lugn = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fin || lugn) return;

    let ram = 0;
    const flytta = (e: PointerEvent) => {
      cancelAnimationFrame(ram);
      ram = requestAnimationFrame(() => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const dx = e.clientX - (r.left + r.width * OGON_X);
        const dy = e.clientY - (r.top + r.height * OGON_Y);
        const avstand = Math.hypot(dx, dy) || 1;
        const langd = Math.min(MAX_BLICK, avstand / 30);
        setBlick({ x: (dx / avstand) * langd, y: (dy / avstand) * langd });
      });
    };
    window.addEventListener('pointermove', flytta);
    return () => {
      window.removeEventListener('pointermove', flytta);
      cancelAnimationFrame(ram);
    };
  }, []);

  return (
    // Ramen slutar exakt vid kortets överkant och klipper resten: ELvis är
    // högre än kortet, så att bara gömma honom bakom det läckte fötterna
    // under kortets nederkant. 20 px luft upptill för hoppet när han blir glad.
    // Höjd = 52 % av bildens höjd (349 × 535) + luften.
    <div
      aria-hidden
      className="pointer-events-none absolute bottom-full right-4 h-[90px] w-[88px] overflow-hidden sm:right-6 sm:h-[103px] sm:w-[104px]"
    >
      <div ref={ref} className="elvis-titt absolute inset-x-0 top-5">
        <MaskotElvis lage={lage} blick={lage === 'vila' ? blick : undefined} />
      </div>
    </div>
  );
}
